import React, { useState, useEffect, FocusEvent, KeyboardEvent } from 'react';
import classNames from 'classnames';
import { IZipInputProps } from '../../../../interfaces';
import { INVALID_CLASS, VALID_CLASS } from '../../../hooks/useValidationHook';
import styles from './ZipCodeInput.module.css';

const validateZipCode = (zipCode: string, country?: string) => {
  const trimmedZipCode = zipCode.replace(/ /g, '');
  if (country === 'US') {
    return trimmedZipCode.length === 5;
  }
  if (country === 'CA') {
    return trimmedZipCode.length === 6;
  }
  if (country === 'GB') {
    return trimmedZipCode.length >= 6 && trimmedZipCode.length <= 8;
  }
  return false;
};

const ZipCodeInput: React.FunctionComponent<IZipInputProps> = ({
  label,
  shipZipError,
  handleInput,
  shipCountry,
  shipState,
}) => {
  const [zipCode, setZipCode] = useState('');
  const [hasBeenValidated, setHasBeenValidated] = useState(false);
  const [validationClass, setValidationClass] = useState('');
  const inputType = shipCountry === 'US' ? 'number' : 'text';
  const hasError = validationClass === INVALID_CLASS;

  const getIsDisabled = () => {
    if (shipCountry === 'GB') return false;
    return !shipCountry || !shipState;
  };

  const getMaxLength = () => {
    if (shipCountry === 'US') return 5;
    // these values account for if the user has added a space to the zip code, which is common for these countries
    if (shipCountry === 'CA') return 7;
    if (shipCountry === 'GB') return 9;
  };

  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setZipCode(value);
    handleInput?.(e);
    if (hasBeenValidated) {
      // we only want to validate on change if the input has already been validated once on blur
      const isValid = validateZipCode(value, shipCountry);
      setValidationClass(isValid ? VALID_CLASS : INVALID_CLASS);
    }
  };

  const handleOnBlur = (e: FocusEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>) => {
    const isValid = validateZipCode(zipCode, shipCountry);
    setValidationClass(isValid ? VALID_CLASS : INVALID_CLASS);
    setHasBeenValidated(true);
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleOnBlur(e);
    }
  };

  useEffect(() => {
    if (shipZipError) {
      setValidationClass(INVALID_CLASS);
    }
  }, [shipZipError]);

  useEffect(() => {
    setZipCode('');
    setValidationClass('');
  }, [shipCountry, shipState]);

  return (
    <div className={styles.inputContainer}>
      <label htmlFor="shipZip" className={styles.label}>
        {label}
        <input
          name="shipZip"
          className={classNames(styles.input, validationClass)}
          type={inputType}
          value={zipCode}
          onChange={handleZipCodeChange}
          onBlur={handleOnBlur}
          onKeyDown={handleOnKeyDown}
          placeholder={`Enter ${label}`}
          disabled={getIsDisabled()}
          data-testid={'shipZip'}
          inputMode={inputType === 'number' ? 'numeric' : undefined}
          maxLength={getMaxLength()}
        />
        {hasError && (
          <p className={styles.inputError}>{`Please enter a valid ${label.toLowerCase()}.`}</p>
        )}
      </label>
    </div>
  );
};

export default ZipCodeInput;
